import React, { useState, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Save, Share2, ShoppingBag, Check } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useApp } from "@/context/AppContext";
import { CASES, DIALS, HANDS, STRAPS, COLOR_HEX, COLOR_LABELS, DEFAULT_CONFIG, calculatePrice, BASE_PRICE, DIMENSION_SURCHARGE } from "@/data/watchData";
import { getWatchLayers } from "@/data/watchRender";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { key: "case",   label_key: "case" },
  { key: "dial",   label_key: "dial" },
  { key: "hands",  label_key: "hands" },
  { key: "strap",  label_key: "strap" },
  { key: "result", label_key: "result" },
];

const CONFIG_KEYS = { case: "caseId", dial: "dialId", hands: "handsId", strap: "strapId" };

const Configurator = () => {
  const { t, lang, addToCart, customOptions } = useApp();

  // Admin-added colors/images get appended to the built-in option lists so
  // they just show up as extra swatches, per category.
  const OPTIONS_MAP = useMemo(() => {
    const extra = (cat) => customOptions.filter(o => o.category === cat).map(o => ({ id: o.id, color: o.color_hex, label: o.label, isCustom: true }));
    return {
      case: [...CASES, ...extra("case")],
      dial: [...DIALS, ...extra("dial")],
      hands: [...HANDS, ...extra("hands")],
      strap: [...STRAPS, ...extra("strap")],
    };
  }, [customOptions]);
  const [searchParams] = useSearchParams();
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [activeTab, setActiveTab] = useState("case");
  const [shareUrl, setShareUrl] = useState(null);
  const [touched, setTouched] = useState({ case: false, dial: false, hands: false, strap: false });

  useEffect(() => {
    const share = searchParams.get("share");
    if (share) {
      axios.get(`${API}/configurations/${share}`).then(res => {
        const c = res.data.config;
        setConfig({
          caseId: c.case, dialId: c.dial, handsId: c.hands, strapId: c.strap, engraving: c.engraving || "",
        });
        setTouched({ case: true, dial: true, hands: true, strap: true });
        toast.success(lang === "de" ? "Konfiguration geladen" : "Configuration loaded");
      }).catch(() => toast.error(lang === "de" ? "Konfiguration nicht gefunden" : "Configuration not found"));
    }
  }, [searchParams, lang]);

  const price = useMemo(() => calculatePrice({ touched, engraving: config.engraving }), [touched, config.engraving]);

  const updateOption = (tab, value) => {
    const key = CONFIG_KEYS[tab];
    setConfig(c => ({ ...c, [key]: value }));
    setTouched(t => ({ ...t, [tab]: true }));
  };

  // Real material layered preview:
  //  bottom → STRAP (rotated 90° so both leather pieces flank the case top+bottom)
  //  → CASE (brushed 316L steel base, hue-shifted to Gold / Rose Gold / PVD Black)
  //  → DIAL (green or black photo base depending on target color)
  //  → HANDS (silver base, hue-shifted for gold/rose/black)
  //
  // The layer sources + filters come from the shared `watchRender` module so
  // this preview, the cart drawer and the checkout summary always render
  // the exact same configured watch — never a generic placeholder.
  const layers = useMemo(() => getWatchLayers({
    case: config.caseId, dial: config.dialId, hands: config.handsId, strap: config.strapId,
  }, customOptions), [config.caseId, config.dialId, config.handsId, config.strapId, customOptions]);

  const dialFilter  = layers.dial.filter;
  const caseFilter  = layers.case.filter;
  const handsFilter = layers.hands.filter;
  const strapFilter = layers.strap.filter;

  const handleAdd = () => {
    const finalConfig = {
      case: config.caseId, dial: config.dialId, hands: config.handsId, strap: config.strapId,
      engraving: config.engraving, price,
    };
    addToCart({
      type: "custom",
      name: `SOMMET Custom${config.engraving ? ` · ${config.engraving}` : ""}`,
      price,
      quantity: 1,
      config: finalConfig,
      // No static placeholder image: CartDrawer/Checkout render the real
      // configuration live from `config` via <WatchConfigPreview />.
      image: null,
    });
    toast.success(lang === "de" ? "Zum Warenkorb hinzugefügt" : "Added to cart");
  };

  const handleSave = async () => {
    try {
      const res = await axios.post(`${API}/configurations`, {
        config: {
          case: config.caseId, dial: config.dialId, hands: config.handsId, strap: config.strapId,
          engraving: config.engraving, price,
        },
      });
      const url = `${window.location.origin}/configurator?share=${res.data.share_id}`;
      setShareUrl(url);
      await navigator.clipboard.writeText(url).catch(() => {});
      toast.success(lang === "de" ? "Konfiguration gespeichert & Link kopiert" : "Configuration saved & link copied");
    } catch {
      toast.error(lang === "de" ? "Speichern fehlgeschlagen" : "Save failed");
    }
  };

  const handleShare = async () => {
    try {
      // Save first (or reuse existing link)
      let url = shareUrl;
      if (!url) {
        const res = await axios.post(`${API}/configurations`, {
          config: {
            case: config.caseId, dial: config.dialId, hands: config.handsId, strap: config.strapId,
            engraving: config.engraving, price,
          },
        });
        url = `${window.location.origin}/configurator?share=${res.data.share_id}`;
        setShareUrl(url);
      }
      // Use Web Share API if available
      if (navigator.share) {
        await navigator.share({
          title: "SOMMET ZÜRICH",
          text: lang === "de" ? "Schau dir meine SOMMET-Uhr an:" : "Check out my SOMMET watch:",
          url,
        }).catch(() => {});
      } else {
        await navigator.clipboard.writeText(url).catch(() => {});
        toast.success(lang === "de" ? "Link kopiert!" : "Link copied!");
      }
    } catch {
      toast.error(lang === "de" ? "Teilen fehlgeschlagen" : "Share failed");
    }
  };

  const currentValue = config[CONFIG_KEYS[activeTab]];
  // strap ids ("steel"/"leather") are a material choice, not a color key —
  // resolve to the actual color for swatch/label display.
  const currentOpt = OPTIONS_MAP[activeTab]?.find(o => o.id === currentValue);
  const currentColorKey = activeTab === "strap"
    ? (currentOpt?.color || currentValue)
    : currentValue;

  return (
    <main data-testid="configurator-page" className="pt-28 pb-24 min-h-screen bg-[#170F09]">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="mb-16">
          <div className="kicker kicker-eyebrow mb-3">{t.configurator.kicker}</div>
          <h1 className="section-heading text-4xl md:text-5xl lg:text-6xl">{t.configurator.title}</h1>
        </div>

        <div className="grid lg:grid-cols-12 gap-10">

          {/* ============ PREVIEW ============ */}
          <div className="lg:col-span-7 relative">
            <div
              className="corner-frame relative flex items-center justify-center min-h-[560px] p-8 overflow-hidden border border-white/8"
              style={{
                background: "radial-gradient(ellipse at center, rgba(44,30,16,0.9) 0%, rgba(23,15,9,1) 80%)",
              }}
            >
              <div className="absolute top-6 left-6 z-10">
                <div className="kicker">Preview</div>
              </div>
              <div className="absolute top-6 right-6 z-10 text-right">
                <div className="kicker">CHF</div>
                <div data-testid="configurator-price" className="font-heading text-4xl md:text-5xl text-[#C9A36B]">{price.toFixed(2)}</div>
              </div>
              <span className="corner-frame-tr" />
              <span className="corner-frame-bl" />

              {/* Preview stage.
                    - Isolated tabs (case/dial/hands/strap) show a large showcase photo.
                    - "result" tab stacks the master layers (all 1400×1400) with `absolute inset:0`
                       so every part sits at its exact anatomical position. */}
              <div className="relative aspect-square w-full max-w-[520px] overflow-hidden" data-testid="watch-stage">

                {activeTab === "result" ? (
                  <>
                    {/* Layer 1 — STRAP (bottom) */}
                    <img
                      src={layers.strap.src}
                      alt=""
                      aria-hidden="true"
                      data-testid="config-strap-layer"
                      className="absolute inset-0 w-full h-full object-contain transition-[filter] duration-500 pointer-events-none"
                      style={{ filter: `${strapFilter} drop-shadow(0 10px 22px rgba(0,0,0,0.55))` }}
                    />
                    {/* Layer 2 — CASE */}
                    <img
                      src={layers.case.src}
                      alt="SOMMET"
                      data-testid="config-case-layer"
                      className="absolute inset-0 w-full h-full object-contain transition-[filter] duration-500"
                      style={{ filter: `${caseFilter} drop-shadow(0 10px 22px rgba(0,0,0,0.6))` }}
                    />
                    {/* Layer 3 — DIAL */}
                    <img
                      src={layers.dial.src}
                      alt=""
                      aria-hidden="true"
                      data-testid="config-dial-layer"
                      className="absolute inset-0 w-full h-full object-contain transition-[filter] duration-500 pointer-events-none"
                      style={{ filter: dialFilter }}
                    />
                    {/* Layer 4 — HANDS */}
                    <img
                      src={layers.hands.src}
                      alt=""
                      aria-hidden="true"
                      data-testid="config-hands-layer"
                      className="absolute inset-0 w-full h-full object-contain transition-[filter] duration-500 pointer-events-none"
                      style={{ filter: handsFilter }}
                    />
                  </>
                ) : activeTab === "case" ? (
                  // Same master case photo used everywhere else — it already
                  // fills most of the 1400×1400 canvas, so no extra zoom needed.
                  <img
                    src={layers.case.src}
                    alt="Case"
                    data-testid="config-case-showcase"
                    className="absolute inset-0 m-auto object-contain transition-[filter] duration-500"
                    style={{
                      width: "88%", height: "88%",
                      top: 0, bottom: 0, left: 0, right: 0,
                      filter: `${caseFilter} drop-shadow(0 10px 26px rgba(0,0,0,0.65))`,
                    }}
                  />
                ) : activeTab === "dial" ? (
                  // Same master dial photo as the result view, zoomed in (the
                  // dial only fills the centre of the 1400×1400 canvas).
                  <img
                    src={layers.dial.src}
                    alt="Dial"
                    data-testid="config-dial-showcase"
                    className="absolute inset-0 w-full h-full object-contain transition-[filter] duration-500"
                    style={{
                      transform: "scale(2.3)",
                      filter: `${dialFilter} drop-shadow(0 10px 26px rgba(0,0,0,0.55))`,
                    }}
                  />
                ) : activeTab === "hands" ? (
                  // Same master hands photo as the result view, zoomed in.
                  <img
                    src={layers.hands.src}
                    alt="Hands"
                    data-testid="config-hands-showcase"
                    className="absolute inset-0 w-full h-full object-contain transition-[filter] duration-500"
                    style={{
                      transform: "scale(2)",
                      filter: `${handsFilter} drop-shadow(0 8px 22px rgba(0,0,0,0.55))`,
                    }}
                  />
                ) : activeTab === "strap" ? (
                  // Same master strap photo as the result view. The strap only
                  // occupies bands near the top/bottom of the canvas, so this
                  // is zoomed and nudged up to frame the top band clearly.
                  <img
                    src={layers.strap.src}
                    alt="Strap"
                    data-testid="config-strap-showcase"
                    className="absolute inset-0 w-full h-full object-contain transition-[filter] duration-500"
                    style={{
                      transform: "scale(1.9) translateY(8%)",
                      filter: `${strapFilter} drop-shadow(0 14px 34px rgba(0,0,0,0.6))`,
                    }}
                  />
                ) : null}
              </div>

              {/* Hands color reflected directly on the watch via SVG overlay */}
            </div>

            {/* Summary strip with clear color swatches */}
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4 border-t border-white/10 pt-6">
              {[
                { key: "case", value: config.caseId },
                { key: "dial", value: config.dialId },
                { key: "hands", value: config.handsId },
                { key: "strap", value: config.strapId },
              ].map(item => (
                <div key={item.key} data-testid={`summary-${item.key}`} className="flex items-center gap-3">
                  <span
                    className="w-8 h-8 rounded-full border-2 border-white/20 shrink-0 shadow-lg"
                    style={{ background: COLOR_HEX[item.value] }}
                  />
                  <div>
                    <div className="kicker">{t.configurator[item.key]}</div>
                    <div className="text-sm text-white/90 mt-0.5">{COLOR_LABELS[lang][item.value]}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ============ CONTROLS ============ */}
          <div className="lg:col-span-5 space-y-8">
            <div className="flex md:grid md:grid-cols-5 gap-0 overflow-x-auto border-b border-white/10 no-scrollbar">
              {TABS.map(tab => (
                <button
                  key={tab.key}
                  data-testid={`configurator-tab-${tab.key}`}
                  onClick={() => setActiveTab(tab.key)}
                  className={`shrink-0 whitespace-nowrap px-4 md:px-2 py-4 font-mono text-[0.62rem] tracking-[0.15em] md:tracking-[0.2em] uppercase transition-all border-b-2 ${
                    activeTab === tab.key
                      ? "border-[#C9A36B] text-[#C9A36B]"
                      : "border-transparent text-white/50 hover:text-white/80"
                  }`}
                >
                  {t.configurator[tab.label_key]}
                </button>
              ))}
            </div>

            <div className="min-h-[220px]">
              {activeTab === "result" ? (
                /* ============ RESULT VIEW: recap of full configuration ============ */
                <div className="space-y-6" data-testid="result-panel">
                  <div>
                    <div className="kicker mb-2">{lang === "de" ? "Deine SOMMET" : "Your SOMMET"}</div>
                    <div className="font-heading text-3xl md:text-4xl text-white leading-tight">
                      {lang === "de" ? "Fertig konfiguriert" : "Fully configured"}
                    </div>
                    <p className="text-sm text-white/55 mt-2 max-w-md">
                      {lang === "de"
                        ? "Fein zusammengestellt aus vier Elementen. Jetzt in den Warenkorb legen oder speichern."
                        : "Carefully composed from four elements. Add to cart now or save for later."}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {[
                      { key: "case",  value: config.caseId,  extra: OPTIONS_MAP.case.find(o => o.id === config.caseId)?.material?.[lang] },
                      { key: "dial",  value: config.dialId },
                      { key: "hands", value: config.handsId },
                      { key: "strap", value: config.strapId, extra: OPTIONS_MAP.strap.find(o => o.id === config.strapId)?.material?.[lang] },
                    ].map(item => {
                      const opt = OPTIONS_MAP[item.key]?.find(o => o.id === item.value);
                      // Case/dial/hands ids double as their own color key, but
                      // strap ids ("steel"/"leather") are a material choice —
                      // resolve those to the color they should display.
                      const colorKey = item.key === "strap" ? (opt?.color || item.value) : item.value;
                      const hex = opt?.isCustom ? opt.color : COLOR_HEX[colorKey];
                      const label = opt?.isCustom ? opt.label : COLOR_LABELS[lang][colorKey];
                      return (
                        <div key={item.key} data-testid={`result-${item.key}`} className="flex items-center gap-3 border border-white/8 bg-white/[0.02] p-3">
                          <span
                            className="w-9 h-9 rounded-full border-2 border-white/20 shrink-0 shadow-lg"
                            data-metal={!opt?.isCustom && ["silver","gold","rose_gold","bronze"].includes(colorKey) ? "1" : undefined}
                            style={{ background: hex }}
                          />
                          <div className="min-w-0">
                            <div className="kicker">{t.configurator[item.key]}</div>
                            <div className="text-sm text-white/90 mt-0.5 truncate">{label}</div>
                            {item.extra && (
                              <div className="text-[0.6rem] text-white/40 font-mono tracking-widest uppercase mt-0.5 truncate">{item.extra}</div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="flex justify-between border-t border-white/10 pt-4">
                    <span className="kicker">{t.configurator.price}</span>
                    <span className="font-heading text-2xl text-[#C9A36B]">CHF {price.toFixed(2)}</span>
                  </div>
                </div>
              ) : (
                /* ============ OPTION PICKER ============ */
                <div>
                  <div className="mb-8 flex items-center gap-4">
                    <span
                      className="w-14 h-14 rounded-full border-2 border-[#C9A36B] shrink-0 shadow-[0_0_30px_rgba(201,163,107,0.25)]"
                      style={{ background: currentOpt?.isCustom ? currentOpt.color : COLOR_HEX[currentColorKey] }}
                    />
                    <div>
                      <div className="kicker mb-1">{t.configurator[activeTab]}</div>
                      <div className="font-heading text-3xl text-white">
                        {currentOpt?.isCustom ? currentOpt.label : COLOR_LABELS[lang][currentColorKey]}
                      </div>
                      {(activeTab === "case" || activeTab === "strap") && (
                        <div className="text-xs text-white/45 mt-1 font-mono tracking-wider uppercase">
                          {OPTIONS_MAP[activeTab].find(o => o.id === currentValue)?.material?.[lang]}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-5 mb-8">
                    {OPTIONS_MAP[activeTab].map(opt => {
                      const isMetal = !opt.isCustom && ["silver","gold","rose_gold","bronze"].includes(opt.color);
                      const isActive = currentValue === opt.id;
                      const hex = opt.isCustom ? opt.color : COLOR_HEX[opt.color];
                      const label = opt.isCustom ? opt.label : COLOR_LABELS[lang][opt.color];
                      return (
                        <button
                          key={opt.id}
                          data-testid={`swatch-${activeTab}-${opt.id}`}
                          onClick={() => updateOption(activeTab, opt.id)}
                          className={`swatch-item ${isActive ? "is-active" : ""}`}
                          aria-label={label}
                          aria-pressed={isActive}
                        >
                          <span
                            className={`swatch-lg ${isActive ? "active" : ""}`}
                            style={{ background: hex }}
                            data-metal={isMetal ? "1" : undefined}
                            title={label}
                          />
                          <span className="swatch-label">{label}</span>
                        </button>
                      );
                    })}
                  </div>

                  <div className="flex justify-between text-sm border-t border-white/10 pt-4">
                    <span className="text-white/50">
                      {touched[activeTab]
                        ? "+ CHF " + DIMENSION_SURCHARGE[activeTab]
                        : lang === "de" ? "Standard inklusive" : "Included by default"}
                    </span>
                    <span className="font-mono text-white/40">Base CHF {BASE_PRICE}</span>
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-3">
              <button
                data-testid="add-to-cart-button"
                onClick={handleAdd}
                className="btn-champagne w-full py-5"
              >
                <ShoppingBag size={16} /> {t.configurator.addToCart} — CHF {price.toFixed(2)}
              </button>
              <div className="grid grid-cols-2 gap-3">
                <button
                  data-testid="save-config-button"
                  onClick={handleSave}
                  className="btn-ghost flex items-center justify-center gap-2"
                >
                  <Save size={14} /> {t.configurator.save}
                </button>
                <button
                  data-testid="share-config-button"
                  onClick={handleShare}
                  className="btn-ghost flex items-center justify-center gap-2"
                >
                  <Share2 size={14} /> {t.configurator.share}
                </button>
              </div>
            </div>

            {shareUrl && (
              <div data-testid="share-url-display" className="border border-[#C9A36B]/40 bg-[#C9A36B]/5 p-5 space-y-3">
                <div className="kicker text-[#C9A36B]">✓ {lang === "de" ? "Deine Konfiguration wurde gespeichert" : "Your configuration was saved"}</div>
                <div className="flex gap-2 items-center">
                  <input
                    readOnly
                    value={shareUrl}
                    className="flex-1 bg-transparent border-b border-white/20 text-white/85 text-xs py-2 outline-none font-mono"
                    onFocus={(e) => e.target.select()}
                  />
                  <button
                    data-testid="copy-share-url"
                    onClick={() => {
                      navigator.clipboard.writeText(shareUrl).catch(() => {});
                      toast.success(lang === "de" ? "Kopiert!" : "Copied!");
                    }}
                    className="text-xs font-mono uppercase tracking-widest text-[#C9A36B] hover:text-white transition-colors px-3 py-2 border border-[#C9A36B]/40"
                  >
                    {lang === "de" ? "Kopieren" : "Copy"}
                  </button>
                </div>
                <p className="text-xs text-white/50 leading-relaxed">
                  {lang === "de"
                    ? "Teile diesen Link mit uns oder Freunden — jeder öffnet deine exakte Konfiguration."
                    : "Share this link with us or friends — they'll open your exact configuration."}
                </p>
              </div>
            )}

            <div className="border-t border-white/10 pt-6 space-y-4">
              {t.advantages.items.map((f, i) => (
                <div key={i} className="flex items-start gap-3 text-sm">
                  <Check size={14} strokeWidth={1.6} className="text-[#C9A36B] mt-0.5 shrink-0" />
                  <div>
                    <span className="text-white/80">{f.title}</span>
                    <span className="text-white/40 ml-2">— {f.desc.split('.')[0]}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </main>
  );
};

export default Configurator;
